/**
 * The Rat Swarm combat sound cues (ADR-0116/ADR-0120). Two one-shot effects, both
 * fired off the server's one-shot action-animation broadcast (the `attack`/`die`
 * clips) rather than the snapshot stream, so - like {@link awardCue} and {@link
 * bankCue} - the *decision* here is pure and unit-tested, separate from {@link
 * AudioManager}'s *playback*:
 *
 *  - **Swing grunt**: when the *local* player's stomp swing plays, a random human
 *    "oomph" grunt, picked from the male or female set by the player's body sex.
 *    It is the player's own effort sound (always on top of the listener), so it is
 *    full volume, not proximity-scaled.
 *  - **Rat squeal**: when any rat's `die` clip plays, a random rat death squeal.
 *    Rats die all over the map, so the caller proximity-scales it (like footsteps)
 *    and passes the resolved volume in; a squeal out of earshot is dropped there.
 *
 * The random pick is injectable (`rng`, defaulting to `Math.random`) so tests pin
 * the selection deterministically. Playback still routes through {@link
 * AudioManager}, so the master mute (settings panel) governs both cues.
 */
import type { SoundEvent, SoundId } from './soundObserver';
import {
  ENERGY_VORTEX_BODY,
  LLAMA_BODY,
  monsterSoundFamilyForBody,
  monsterSoundId,
  type MonsterSoundRole,
  type EffectId,
} from '../sim/content';

/** UO body id for a male character (see {@link LoginMessage.sex}); anything else is female. */
const MALE_BODY = 400;

/** A non-empty sound set, so a random pick is always defined (`list[0]` is guaranteed). */
type SoundSet = readonly [SoundId, ...SoundId[]];

/** The male stomp-grunt set, chosen from at random for a male player's swing. */
const MALE_GRUNTS: SoundSet = ['ugh-m-1', 'ugh-m-2', 'ugh-m-3', 'ugh-m-4', 'ugh-m-5'];
/** The female stomp-grunt set, chosen from at random for a female player's swing. */
const FEMALE_GRUNTS: SoundSet = ['ugh-f-1', 'ugh-f-2', 'ugh-f-3'];
/** The rat death-squeal set, chosen from at random on each rat's `die` clip. */
const RAT_SQUEALS: SoundSet = ['rat-die-1', 'rat-die-2', 'rat-die-3'];
/** The energy-vortex wind-gust set, chosen from at random on each vortex attack swing (ADR-0152). */
const VORTEX_GUSTS: SoundSet = ['vortex-attack-1', 'vortex-attack-2', 'vortex-attack-3'];
/**
 * The chaos-llama variant's bark set (ADR-0162), chosen from at random on a llama-bodied
 * vortex's attack or movement in place of the wind gust. Deliberately **skips `llama3`** -
 * exactly `llama1/2/4/5` per the captain's pack.
 */
const LLAMA_BRAYS: SoundSet = ['llama-1', 'llama-2', 'llama-4', 'llama-5'];

// Duel combat sets (PRD #252, ADR-0131). Gendered where the sound is a body's own
// voice (get-hit grunt, death cry); shared where it is not (the whiff of a missed
// punch, the resurrection chime).
/** Male get-hit grunt set, chosen from at random when a male duelist takes a hit. */
const MALE_HITS: SoundSet = ['hit-m-1', 'hit-m-2', 'hit-m-3'];
/** Female get-hit grunt set. */
const FEMALE_HITS: SoundSet = ['hit-f-1', 'hit-f-2', 'hit-f-3'];
/** The shared whiff set, chosen from at random on a missed swing. */
const WHIFFS: SoundSet = ['whiff-1', 'whiff-2', 'whiff-3'];
/** Male death-cry set, chosen from at random on a male duelist's killing blow. */
const MALE_DEATHS: SoundSet = ['death-m-1', 'death-m-2'];
/** Female death-cry set. */
const FEMALE_DEATHS: SoundSet = ['death-f-1', 'death-f-2'];

/** The swing grunt is the player's own effort sound, always at full volume. */
const SWING_VOLUME = 1;

/** Uniformly pick one entry from a non-empty set using the injected `rng` in `[0, 1)`. */
function pick(list: SoundSet, rng: () => number): SoundId {
  return list[Math.min(list.length - 1, Math.floor(rng() * list.length))] ?? list[0];
}

/**
 * The grunt to play for the local player's stomp swing: a random pick from the male
 * set when `sex` is the male body (400), the female set otherwise. Full volume - it
 * is the local player's own effort sound, not a sound positioned in the world.
 */
export function swingCue(sex: number, rng: () => number = Math.random): SoundEvent {
  const set = sex === MALE_BODY ? MALE_GRUNTS : FEMALE_GRUNTS;
  return { sound: pick(set, rng), volume: SWING_VOLUME };
}

/**
 * The squeal to play for a rat's death: a random pick from the rat set at the given
 * `volume` (the caller resolves it by proximity to the local player). Returns `null`
 * when `volume <= 0` - a rat that died out of earshot makes no sound - so the caller
 * can play the result unconditionally.
 */
export function ratDeathCue(volume: number, rng: () => number = Math.random): SoundEvent | null {
  if (volume <= 0) return null;
  return { sound: pick(RAT_SQUEALS, rng), volume };
}

/**
 * The cue a **vortex-kind creature** makes, picked by its `body` (Blackthorn's Storm,
 * ADR-0152 + ADR-0162): a random {@link VORTEX_GUSTS wind gust} for a normal energy
 * vortex ({@link ENERGY_VORTEX_BODY}), a random {@link LLAMA_BRAYS llama bark} for a
 * {@link LLAMA_BODY chaos-llama} variant, at the given `volume` (the caller
 * proximity-scales it by tile distance to the local player, the rat-squeal precedent -
 * vortexes roam and bite all over the bank). One decision keyed off the replicated body
 * id so both the attack swing (`onActionAnimation`) and the movement roam
 * ({@link ./soundObserver}) sound the right creature. Returns `null` when the body is not
 * a vortex-kind creature, or when `volume <= 0` (out of earshot / silenced), so the
 * caller can play the result unconditionally.
 */
export function vortexCreatureCue(
  body: number | undefined,
  volume: number,
  rng: () => number = Math.random,
): SoundEvent | null {
  if (volume <= 0) return null;
  if (body === LLAMA_BODY) return { sound: pick(LLAMA_BRAYS, rng), volume };
  if (body === ENERGY_VORTEX_BODY) return { sound: pick(VORTEX_GUSTS, rng), volume };
  return null;
}

/**
 * The cue a **bestiary monster** makes for one of its four action roles (ADR-0187):
 * move (its roaming idle voice), attack, hurt (damaged), die - keyed off the
 * replicated body id via the creature's sound family, the {@link vortexCreatureCue}
 * pattern. Unlike the vortex/duel sets there is no random pick: UO records exactly
 * one sound per creature per role, so the cue is fully determined by (body, role).
 * The caller resolves `volume` by proximity to the local player (monsters roam,
 * bite, and die all over the arena). Returns `null` for a non-bestiary body or when
 * `volume <= 0`, so the caller can play the result unconditionally.
 */
export function monsterCue(
  body: number | undefined,
  role: MonsterSoundRole,
  volume: number,
): SoundEvent | null {
  if (volume <= 0) return null;
  const family = monsterSoundFamilyForBody(body);
  if (!family) return null;
  return { sound: monsterSoundId(family, role), volume };
}

/**
 * The classic UO spell sound per baked arena effect (ADR-0187; ServUO PlaySound
 * ids). Partial over {@link EffectId}: only the arena *spell* strips have a spell
 * sound - the moongate strips (PRD #362) carry their own gate cues via the
 * SoundObserver, so they are deliberately absent here and {@link spellCue}
 * returns `null` for them.
 */
const SPELL_SOUNDS: Partial<Record<EffectId, SoundId>> = {
  'magic-arrow': 'spell-magic-arrow',
  fireball: 'spell-fireball',
  explosion: 'spell-explosion',
  'flame-strike': 'spell-flame-strike',
  poison: 'spell-poison',
  lightning: 'spell-lightning',
  'greater-heal': 'spell-greater-heal',
  cure: 'spell-cure',
  // The archer's physical shots (ADR-0237): the bow twang / crossbow release,
  // sounded off the arrow/bolt flight FX (this same spellCue path).
  arrow: 'bow-shot',
  bolt: 'crossbow-shot',
  // The King's Justice smoke poof (ADR-0194): not an arena spell, but the effects
  // registry is one namespace - mapped to its own teleport-poof sound so the record
  // stays total over EffectId (the JusticeFx handler plays it directly).
  poof: 'poof',
};

/**
 * The sound one arena spell effect makes (ADR-0187), played at the volley show's own
 * instants - a projectile's launch, an explosion/flame-strike's landing - via the
 * renderer's VolleyFxHooks seam. Fully determined by the effect id (the classic
 * spells each have exactly one effect sound); the caller owns the volume and the
 * cascade throttle (a maxed volley is ~100 ops). Returns `null` when `volume <= 0`
 * so the caller can play the result unconditionally.
 */
export function spellCue(effect: EffectId, volume: number): SoundEvent | null {
  if (volume <= 0) return null;
  const sound = SPELL_SOUNDS[effect];
  if (!sound) return null;
  return { sound, volume };
}

// ---------------------------------------------------------------------------
// Duel combat cues (PRD #252, ADR-0131). Each is played at a `volume` the caller
// resolves: **full** volume for a participant's own fight (the sound is on top of
// the listener, like the swing grunt), **proximity-scaled** for a bystander (the
// footstep/rat-squeal precedent). All return `null` when `volume <= 0` (out of
// earshot) so the caller can play the result unconditionally. A swing carries no
// separate effort sound - its audible result is the victim's hit grunt on a
// landing or the whiff on a miss.
// ---------------------------------------------------------------------------

/**
 * The get-hit grunt for a landed duel blow: a random pick from the male set when
 * the **victim's** body is male (400), the female set otherwise, at the resolved
 * `volume`. `null` out of earshot.
 */
export function hitGruntCue(
  victimSex: number,
  volume: number,
  rng: () => number = Math.random,
): SoundEvent | null {
  if (volume <= 0) return null;
  return { sound: pick(victimSex === MALE_BODY ? MALE_HITS : FEMALE_HITS, rng), volume };
}

/** The shared whiff for a missed swing, at the resolved `volume`. `null` out of earshot. */
export function whiffCue(volume: number, rng: () => number = Math.random): SoundEvent | null {
  if (volume <= 0) return null;
  return { sound: pick(WHIFFS, rng), volume };
}

/**
 * The bow twang of a fired duel shot (Fire your bow!, ADR-0255; auto-fire ADR-0275), at the
 * resolved `volume` - full for the shooter's own fight, proximity-scaled for a
 * bystander (the swing-cue rule). It sounds on the `shoot-bow` clip regardless of
 * hit or miss (the arrow flies either way), so unlike a punch the shot has its own
 * audible release; a landing hit still plays the victim's separate get-hit grunt off
 * the flinch clip. Reuses the archer's `bow-shot` sound (ADR-0237). `null` out of earshot.
 */
export function duelBowCue(volume: number): SoundEvent | null {
  if (volume <= 0) return null;
  return { sound: 'bow-shot', volume };
}

/**
 * The death cry on a killing blow: a random pick from the male set when the
 * **loser's** body is male (400), the female set otherwise, at the resolved
 * `volume`. `null` out of earshot.
 */
export function deathCryCue(
  loserSex: number,
  volume: number,
  rng: () => number = Math.random,
): SoundEvent | null {
  if (volume <= 0) return null;
  return { sound: pick(loserSex === MALE_BODY ? MALE_DEATHS : FEMALE_DEATHS, rng), volume };
}

/** The shared resurrection chime, at the resolved `volume`. `null` out of earshot. */
export function resurrectCue(volume: number): SoundEvent | null {
  if (volume <= 0) return null;
  return { sound: 'resurrect', volume };
}
